import React from 'react';
import { classNames } from '@/utils/classNames';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showPrevNext?: boolean;
  showFirstLast?: boolean;
  maxVisible?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showPrevNext = true,
  showFirstLast = true,
  maxVisible = 5,
  size = 'md',
  className,
}) => {
  if (totalPages <= 1) return null;

  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisible / 2);

    const startPage = Math.max(1, currentPage - halfVisible);
    const endPage = Math.min(totalPages, currentPage + halfVisible);

    // 最初のページが見えない場合
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }

    // 中央のページ番号
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // 最後のページが見えない場合
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  const buttonBaseClasses = classNames(
    'relative inline-flex items-center border border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400',
    'hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300',
    'focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500',
    'transition-colors touch-target',
    sizeClasses[size]
  );

  const activeButtonClasses = classNames(
    buttonBaseClasses,
    'z-10 bg-primary-50 dark:bg-primary-900 border-primary-500 text-primary-600 dark:text-primary-400'
  );

  const disabledButtonClasses = classNames(buttonBaseClasses, 'opacity-50 cursor-not-allowed');

  const renderPageButton = (page: number | string, index: number) => {
    if (page === '...') {
      return (
        <span
          key={`ellipsis-${index}`}
          className={classNames(
            'relative inline-flex items-center border border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300',
            sizeClasses[size]
          )}
        >
          ...
        </span>
      );
    }

    const pageNumber = page as number;
    const isActive = pageNumber === currentPage;

    return (
      <button
        key={pageNumber}
        onClick={() => onPageChange(pageNumber)}
        className={isActive ? activeButtonClasses : buttonBaseClasses}
        aria-current={isActive ? 'page' : undefined}
        aria-label={`ページ ${pageNumber} に移動`}
      >
        {pageNumber}
      </button>
    );
  };

  return (
    <nav
      className={classNames('flex items-center justify-center', className)}
      aria-label='ページネーション'
    >
      <div className='flex -space-x-px rounded-md shadow-sm'>
        {/* First page button */}
        {showFirstLast && currentPage > 1 && (
          <button
            onClick={() => onPageChange(1)}
            className={classNames(buttonBaseClasses, 'rounded-l-md')}
            aria-label='最初のページに移動'
          >
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 9H17a1 1 0 110 2h-5.586l3.293 3.293a1 1 0 010 1.414zM7 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        )}

        {/* Previous page button */}
        {showPrevNext && (
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={classNames(
              currentPage === 1 ? disabledButtonClasses : buttonBaseClasses,
              !showFirstLast && 'rounded-l-md'
            )}
            aria-label='前のページに移動'
          >
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        )}

        {/* Page numbers */}
        {pageNumbers.map((page, index) => renderPageButton(page, index))}

        {/* Next page button */}
        {showPrevNext && (
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={classNames(
              currentPage === totalPages ? disabledButtonClasses : buttonBaseClasses,
              !showFirstLast && 'rounded-r-md'
            )}
            aria-label='次のページに移動'
          >
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        )}

        {/* Last page button */}
        {showFirstLast && currentPage < totalPages && (
          <button
            onClick={() => onPageChange(totalPages)}
            className={classNames(buttonBaseClasses, 'rounded-r-md')}
            aria-label='最後のページに移動'
          >
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M4.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L8.586 11H3a1 1 0 110-2h5.586L4.293 5.707a1 1 0 010-1.414zM13 17a1 1 0 001-1V4a1 1 0 10-2 0v12a1 1 0 001 1z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
